// Boot phase tracking — MUST be first
import { setBootPhase, setBootError, getBootPhase, getBootError } from "./lib/bootGuard";
setBootPhase("boot:started");

// ── Global crash handlers (before React mounts) ────────────────
// If something explodes before React renders, we show a minimal crash screen
// by writing directly to the DOM.
const _originalError = (window as any).__tm_boot_error;
function showPreReactCrashScreen(error: unknown) {
  setBootError(error);
  const msg = error instanceof Error ? error.message : String(error);
  const phase = getBootPhase();
  const lastErr = getBootError();
  try {
    document.body.innerHTML = `
      <div style="position:fixed;inset:0;z-index:999999;background:#fff;color:#1a1a1a;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif">
        <div style="max-width:420px;width:100%;text-align:center">
          <div style="width:64px;height:64px;border-radius:50%;background:#fee2e2;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;font-size:32px">⚠️</div>
          <h1 style="font-size:20px;font-weight:700;margin:0 0 8px">TransportMate failed to start</h1>
          <p style="font-size:14px;color:#666;margin:0 0 16px">A startup error occurred.</p>
          <div style="background:#f5f5f5;border-radius:8px;padding:12px;text-align:left;font-size:11px;line-height:1.5;max-height:200px;overflow:auto;word-break:break-word;margin-bottom:16px;font-family:monospace">
            <div><strong>Error:</strong> ${msg}</div>
            <div><strong>Boot Phase:</strong> ${phase}</div>
            ${lastErr ? `<div><strong>Last Error:</strong> ${lastErr.substring(0, 200)}</div>` : ""}
          </div>
          <button onclick="window.location.reload()" style="padding:10px 20px;font-size:14px;font-weight:600;background:#2563eb;color:#fff;border:none;border-radius:8px;cursor:pointer">Reload</button>
        </div>
      </div>`;
  } catch {}
}

window.onerror = function (message, source, lineno, colno, error) {
  console.error("[GlobalError]", message, source, lineno, colno);
  showPreReactCrashScreen(error || message);
};

window.addEventListener("unhandledrejection", (event) => {
  console.error("[GlobalHandler] Unhandled rejection:", event.reason);
  event.preventDefault(); // Prevent Android WebView from crashing
});

// ── Canonical domain ───────────────────────────────────────────
// Enforced at the edge (apex 302 -> www). A client-side www -> apex
// redirect here fights the edge redirect and causes an infinite reload
// loop in production. Do not reintroduce it.

// ── Path normalisation ─────────────────────────────────────────
// Historic links (e.g. tracking SMS built from an APP_URL with a trailing
// slash) can contain duplicate slashes such as "//track?jobId=…", which the
// router cannot match and renders as 404. Collapse them in place before the
// router mounts. Same-origin, no reload, no effect on well-formed URLs.
try {
  const { pathname, search, hash } = window.location;
  if (/\/{2,}/.test(pathname)) {
    const normalised = pathname.replace(/\/{2,}/g, "/");
    window.history.replaceState(null, "", `${normalised}${search}${hash}`);
  }
} catch {}



// ── Config validation (before React) ───────────────────────────
import { validateConfig } from "./lib/configValidation";
validateConfig(); // throws if required env vars are missing

// ── React app ──────────────────────────────────────────────────
import { createRoot } from "react-dom/client";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BootErrorBoundary } from "./components/BootErrorBoundary";
import App from "./App.tsx";
import "./index.css";
import { initializeNativeApp } from "./lib/capacitor";
import { initOfflineDb } from "./lib/offlineDb";
import { startSyncEngine } from "./lib/syncEngine";
import { startAppraisalUploadQueue } from "./lib/appraisalUploadQueue";
import { startAppraisalEmailQueue } from "./lib/appraisalEmailQueue";
import { startCompletionWorkflowQueue } from "./lib/completionWorkflowQueue";

import { startDeviceStorageGuard } from "./lib/deviceStorageGuard";
import { initOfflineWorkspace } from "./lib/offlineWorkspace";
import { bootstrapDebugMode } from "./lib/debugMode";
import { supabase } from "./integrations/supabase/client";
import { installSupportTenantGuard } from "./lib/supportTenantGuard";
import { installTenantCacheGuard } from "./lib/tenantCacheGuard";
import { initObservability } from "./lib/observability";

// Staging-only isolated inspection. Production/native bundles never take this entry.
const fleetHubInspection = import.meta.env.DEV && (
  window.location.pathname === "/integrations/fleethub-jobs-preview" ||
  (window.location.pathname === "/integrations/fleethub-dev" && new URLSearchParams(window.location.search).get("mock-preview") === "1")
);
const FleetHubPreviewEntry = lazy(() => import("./FleetHubPreviewEntry"));

// Install the tenant boundary before React mounts or any page query can run.
if (!fleetHubInspection) installSupportTenantGuard(supabase);

// P0: bind every client cache to the authenticated identity and purge on
// sign-out / identity change (web AND native).
if (!fleetHubInspection) installTenantCacheGuard(supabase as any);

// Additive, fire-and-forget observability (Phase 12). Never blocks boot.
if (!fleetHubInspection) initObservability();

// Strip ?debug param from URL; debug mode activation is deferred until
// the auth session is available (see App.tsx or auth context).
if (!fleetHubInspection) bootstrapDebugMode();

declare const __BUILD_VERSION__: string;
export const BUILD_VERSION = typeof __BUILD_VERSION__ !== "undefined" ? __BUILD_VERSION__ : "dev";
(window as any).__BUILD_VERSION__ = BUILD_VERSION;
console.log("TransportMate build:", BUILD_VERSION);

// ── Asset integrity check ──────────────────────────────────────
// If the build marker is missing, the web assets may be stale/corrupt.
if (typeof __BUILD_VERSION__ === "undefined") {
  console.warn("[Boot] __BUILD_VERSION__ is undefined — web assets may be out of sync");
}

// ── Mount React ────────────────────────────────────────────────
try {
  const rootElement = document.getElementById("root");
  if (!rootElement) throw new Error("Root element missing");
  const root = createRoot(rootElement);
  root.render(
    fleetHubInspection ? <Suspense fallback={<div role="status">Loading FleetHub preview…</div>}><FleetHubPreviewEntry /></Suspense> :
    <BootErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </BootErrorBoundary>
  );
  setBootPhase("boot:react-mounted");
} catch (e) {
  console.error("[Boot] React mount failed:", e);
  setBootError(e);
  showPreReactCrashScreen(e);
}

// ── Deferred initialization (non-blocking) ─────────────────────
// All Capacitor/plugin and offline DB init happens AFTER React mounts.
// Failures here are logged but never crash the app.
//
// CRITICAL ORDER: Capacitor init (which hides the native splash screen)
// MUST run first, before any potentially-hanging IndexedDB work.
// If initOfflineDb() hangs, the splash must already be dismissed.
async function deferredInit() {
  // 1. Capacitor native features FIRST — hides native splash screen
  try {
    setBootPhase("boot:init-capacitor-start");
    await initializeNativeApp();
    setBootPhase("boot:init-capacitor-ok");
  } catch (e) {
    console.error("[Boot] Capacitor init failed (non-fatal):", e);
    setBootPhase("boot:init-capacitor-failed");
    setBootError(e);
  }

  // 2. Offline DB — may involve IndexedDB which can hang on some devices
  try {
    setBootPhase("boot:init-offline-start");
    await Promise.race([
      initOfflineDb(),
      new Promise((_, reject) => setTimeout(() => reject(new Error("OfflineDB init timed out (8s)")), 8000)),
    ]);
    setBootPhase("boot:init-offline-ok");
  } catch (e) {
    console.error("[Boot] OfflineDB init failed (non-fatal):", e);
    setBootPhase("boot:init-offline-failed");
    setBootError(e);
  }

  // 3. Sync engine (depends on offlineDb being ready)
  try {
    startSyncEngine();
    startAppraisalUploadQueue();
    startAppraisalEmailQueue();
    startCompletionWorkflowQueue();

    initOfflineWorkspace();
    startDeviceStorageGuard();
    console.log("[Boot] Sync engine + offline workspace started");
  } catch (e) {
    console.error("[Boot] Sync engine start failed (non-fatal):", e);
  }

  // 4. Version check — detect stale WebView bundles and auto-reload
  try {
    const { checkForUpdates, startPeriodicVersionCheck } = await import("./lib/versionCheck");
    const reloaded = await checkForUpdates();
    if (reloaded) return; // App is reloading — stop further init
    startPeriodicVersionCheck();
    console.log("[Boot] Version check started");
  } catch (e) {
    console.warn("[Boot] Version check failed (non-fatal):", e);
  }

  // 5. Upload tracking is dormant by default — defer its init completely
  setTimeout(() => {
    import("./lib/uploadJobRepository")
      .then(({ uploadJobRepo }) => uploadJobRepo.init())
      .then(() => console.log("[Boot] Upload job repo initialised (deferred)"))
      .catch((e) => console.warn("[Boot] Upload job repo init failed (non-fatal):", e));
  }, 5000);
}

if (!fleetHubInspection) deferredInit();
